import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ESP32SensorData } from './API';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        marginBottom: theme.spacing(2),
        opacity: '95%'
    },
}));

// Helper function to calculate min and max with 10% padding
const calculateMinWithPadding = (min: number, max: number) => Math.floor(min - (max - min) * 0.1);
const calculateMaxWithPadding = (min: number, max: number) => Math.ceil(max + (max - min) * 0.1);

// define ranges keys
type RangeKeys = 'humidity' | 'pressure' | 'temperature' | 'spo2' | 'heart_rate';

interface ChartConfig {
    dataKey: RangeKeys; // Use RangeKeys type
    strokeColor: string;
    label: string;
}

const SensorDataChart = ({ sensorData }: { sensorData: ESP32SensorData[] }) => {
    const classes = useStyles();
    // Format the data
    const latestData = sensorData.map((data) => ({
        ...data,
        // Format timestamp as YYYY-MM-DD HH:mm:ss
        timestamp: new Date(Date.parse(data.timestamp + 'Z')).toLocaleString('default', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        }), // Convert to local time string
    })).reverse(); // Ensure it's in reverse order

    // Calculate ranges with padding for all chart data
    const ranges: Record<RangeKeys, { min: number; max: number }> = {
        humidity: {
            min: calculateMinWithPadding(
                Math.min(...latestData.map((data) => data.humidity || 0)),
                Math.max(...latestData.map((data) => data.humidity || 100))
            ),
            max: calculateMaxWithPadding(
                Math.min(...latestData.map((data) => data.humidity || 0)),
                Math.max(...latestData.map((data) => data.humidity || 100))
            ),
        },
        pressure: {
            min: calculateMinWithPadding(
                Math.min(...latestData.map((data) => data.pressure || 950)),
                Math.max(...latestData.map((data) => data.pressure || 1050))
            ),
            max: calculateMaxWithPadding(
                Math.min(...latestData.map((data) => data.pressure || 950)),
                Math.max(...latestData.map((data) => data.pressure || 1050))
            ),
        },
        temperature: {
            min: calculateMinWithPadding(
                Math.min(...latestData.map((data) => data.temperature || -30)),
                Math.max(...latestData.map((data) => data.temperature || 50))
            ),
            max: calculateMaxWithPadding(
                Math.min(...latestData.map((data) => data.temperature || -30)),
                Math.max(...latestData.map((data) => data.temperature || 50))
            ),
        },
        spo2: {
            min: calculateMinWithPadding(
                Math.min(...latestData.map((data) => data.spo2 || 95)),
                Math.max(...latestData.map((data) => data.spo2 || 100))
            ),
            max: calculateMaxWithPadding(
                Math.min(...latestData.map((data) => data.spo2 || 95)),
                Math.max(...latestData.map((data) => data.spo2 || 100))
            ),
        },
        heart_rate: {
            min: calculateMinWithPadding(
                Math.min(...latestData.map((data) => data.heart_rate || 60)),
                Math.max(...latestData.map((data) => data.heart_rate || 100))
            ),
            max: calculateMaxWithPadding(
                Math.min(...latestData.map((data) => data.heart_rate || 60)),
                Math.max(...latestData.map((data) => data.heart_rate || 100))
            ),
        }
    };

    // Chart configuration for each dataKey
    const chartConfigs: ChartConfig[] = [
        { dataKey: 'temperature', strokeColor: '#ffc658', label: 'Temperature' },
        { dataKey: 'humidity', strokeColor: '#8884d8', label: 'Humidity' },
        { dataKey: 'pressure', strokeColor: '#82ca9d', label: 'Pressure' },
        { dataKey: 'spo2', strokeColor: '#ff7300', label: 'SpO2' },
        { dataKey: 'heart_rate', strokeColor: '#dc143c', label: 'Heart Rate' },
    ];

    return (
        <div>
            {chartConfigs.map(({ dataKey, strokeColor, label }) => (
                <Paper className={classes.paper}>
                    <ResponsiveContainer key={dataKey} width="100%" height={300}>
                        <LineChart data={latestData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="timestamp" />
                            <YAxis
                                domain={[ranges[dataKey].min, ranges[dataKey].max]} // Ensure the key matches the range
                                tickFormatter={(value) => Math.round(value).toString()}
                            />
                            <Tooltip />
                            <Legend formatter={() => label} />
                            <Line type="monotone" dataKey={dataKey} stroke={strokeColor} activeDot={{ r: 8 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </Paper>
            ))}
        </div>
    );
};

export default SensorDataChart;
